import { useCallback, useEffect, useRef, useState } from "react";
import {
  adminVideos,
  deleteVideo,
  login,
  logout,
  publicVideos,
  removeVideoCaptions,
  replaceVideoCaptions,
  replaceVideoPoster,
  sendAnalytics,
  updateVideo,
  uploadVideo,
  videoBySlug,
} from "./api";
import type { UploadDraft, VideoNote, VideoRecord, Visibility } from "./types";
import {
  averageWatched,
  buildMomentUrl,
  buildStableUrl,
  clampTimestamp,
  clearResumePoint,
  completionRate,
  formatShareTime,
  parseTimestampParam,
  readResumePoint,
  resumeWantsOffer,
  saveResumePoint,
  updateMomentInAddressBar,
} from "./lib/share";
import { CompletionTracker } from "./lib/playback";
import { abortUpload } from "./api";

const BLUE = "#0068D9";

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit", year: "numeric" })
    .format(new Date(value)).toUpperCase();
}

function numericDate(value: string): string {
  const date = new Date(value);
  return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getFullYear()).slice(-2)}`;
}

function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function formatNoteTime(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
}

function indexNumber(position: number): string {
  return String(position + 1).padStart(3, "0");
}

function ArrowIcon({ direction = "right" }: { direction?: "left" | "right" }) {
  return <span aria-hidden="true">{direction === "left" ? "←" : "↗"}</span>;
}

function SiteMark() {
  return (
    <a className="site-mark" href="/" aria-label="The Work Index home">
      <span className="site-mark__line" />
      <span>THE WORK INDEX</span>
      <small>ZAIN HASEEB</small>
    </a>
  );
}

function Loading({ label = "Opening the index" }: { label?: string }) {
  return (
    <main className="state-page">
      <span className="state-page__marker" />
      <p>{label}</p>
    </main>
  );
}

function ErrorPage({ message }: { message: string }) {
  return (
    <main className="state-page">
      <span className="state-page__marker state-page__marker--error" />
      <h1>This drawer could not be opened.</h1>
      <p>{message}</p>
      <a className="text-link" href="/"><ArrowIcon direction="left" /> All work</a>
    </main>
  );
}

function HomePage() {
  const [videos, setVideos] = useState<VideoRecord[] | null>(null);
  const [issue, setIssue] = useState("");

  useEffect(() => {
    publicVideos().then(setVideos).catch((error: Error) => setIssue(error.message));
  }, []);

  if (issue) return <ErrorPage message={issue} />;
  if (!videos) return <Loading />;

  return (
    <div className="home-page">
      <header className="home-header">
        <SiteMark />
        <a className="manage-link" href="/manage">OWNER ACCESS</a>
      </header>
      <section className="home-intro">
        <p className="eyebrow">A LIVING ARCHIVE</p>
        <h1>Useful work,<br />opened up.</h1>
        <p className="home-intro__dek">Demonstrations, field notes, and working ideas shared with enough context to be useful.</p>
      </section>
      <section className="catalog" aria-labelledby="catalog-title">
        <div className="catalog__heading">
          <span id="catalog-title">INDEX</span>
          <span>{String(videos.length).padStart(2, "0")} PUBLIC RECORDS</span>
        </div>
        {videos.length === 0 ? (
          <div className="empty-catalog">
            <span className="empty-catalog__number">000</span>
            <p>The first public work will appear here.</p>
          </div>
        ) : (
          <div className="catalog-grid">
            {videos.map((video, position) => (
              <a className="catalog-card" href={`/v/${video.slug}`} key={video.id}>
                <div className="catalog-card__poster">
                  {video.poster_key ? <img src={`/poster/${video.id}`} alt="" /> : <div className="poster-placeholder" />}
                  <span className="catalog-card__duration">{formatDuration(video.duration_seconds)}</span>
                </div>
                <div className="catalog-card__index">{indexNumber(position)}</div>
                <div className="catalog-card__copy">
                  <p className="eyebrow">DEMONSTRATION · {numericDate(video.created_at)}</p>
                  <h2>{video.title}</h2>
                  <p>{video.description}</p>
                </div>
                <ArrowIcon />
              </a>
            ))}
          </div>
        )}
      </section>
      <footer className="site-footer">
        <span>THE WORK INDEX</span><span>© {new Date().getFullYear()} ZAIN HASEEB</span>
      </footer>
    </div>
  );
}

function WatchPage({ slug }: { slug: string }) {
  const [video, setVideo] = useState<VideoRecord | null>(null);
  const [issue, setIssue] = useState("");
  const [notesOpen, setNotesOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [momentCopied, setMomentCopied] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [resumeOffer, setResumeOffer] = useState<number | null>(null);
  const playerRef = useRef<HTMLVideoElement>(null);
  const playedRef = useRef(false);
  const trackerRef = useRef<CompletionTracker | null>(null);
  const watchedRef = useRef(0);
  const lastTimeRef = useRef(0);
  const lastSaveRef = useRef(0);
  const pendingMomentRef = useRef<number | null>(null);

  useEffect(() => {
    let active = true;
    videoBySlug(slug).then((next) => {
      if (!active) return;
      const requested = parseTimestampParam(new URLSearchParams(window.location.search).get("t"));
      if (requested !== null) {
        pendingMomentRef.current = requested;
      } else {
        const resume = readResumePoint(next.id);
        setResumeOffer(resume && resumeWantsOffer(resume, next.duration_seconds) ? resume.seconds : null);
      }
      setVideo(next);
    }).catch((error: Error) => {
      if (active) setIssue(error.message);
    });
    return () => { active = false; };
  }, [slug]);

  const flushWatch = useCallback(() => {
    if (!video || watchedRef.current < 1) return;
    sendAnalytics(video.id, "watch", Math.round(watchedRef.current));
    watchedRef.current = 0;
  }, [video]);

  useEffect(() => {
    const onPageHide = () => flushWatch();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      flushWatch();
    };
  }, [flushWatch]);

  if (issue) return <ErrorPage message={issue} />;
  if (!video) return <Loading label="Opening this record" />;

  const announce = (message: string) => {
    setAnnouncement("");
    window.setTimeout(() => setAnnouncement(message), 30);
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(buildStableUrl(window.location.origin, video.slug));
    setCopied(true);
    announce("Viewing link copied to clipboard.");
    window.setTimeout(() => setCopied(false), 1800);
  };

  const currentSeconds = () => Math.floor(playerRef.current?.currentTime ?? 0);

  const copyMomentLink = async () => {
    const seconds = currentSeconds();
    await navigator.clipboard.writeText(buildMomentUrl(window.location.origin, video.slug, seconds));
    const label = formatShareTime(seconds);
    setMomentCopied(label);
    announce(`Link at ${label} copied to clipboard.`);
    window.setTimeout(() => setMomentCopied(""), 1800);
  };

  const seekTo = (seconds: number) => {
    const player = playerRef.current;
    if (!player) return;
    player.currentTime = clampTimestamp(seconds, player.duration || video.duration_seconds);
    updateMomentInAddressBar(Math.floor(player.currentTime));
    void player.play().catch(() => {
      // Autoplay policies may block playback; the seek itself still applies.
    });
  };

  const onLoadedMetadata = () => {
    const player = playerRef.current;
    if (!player) return;
    const duration = player.duration || video.duration_seconds;
    trackerRef.current = new CompletionTracker(duration);
    const requested = pendingMomentRef.current;
    pendingMomentRef.current = null;
    if (requested !== null) {
      const target = clampTimestamp(requested, duration);
      player.currentTime = target;
      announce(`Starting at ${formatShareTime(target)}.`);
    }
  };

  const recordCompletion = (previousSeconds: number, ended: boolean) => {
    const tracker = trackerRef.current;
    const player = playerRef.current;
    if (!tracker || !player) return;
    const verdict = tracker.observe(previousSeconds, player.currentTime, ended);
    if (verdict === "count") sendAnalytics(video.id, "complete");
  };

  const acceptResume = () => {
    seekTo(resumeOffer ?? 0);
    setResumeOffer(null);
    announce(`Resumed at ${formatShareTime(resumeOffer ?? 0)}.`);
  };

  const dismissResume = () => {
    setResumeOffer(null);
    clearResumePoint(video.id);
  };

  return (
    <div className={`watch-page ${notesOpen && video.notes.length ? "watch-page--notes" : ""}`}>
      <p className="sr-only" role="status">{announcement}</p>
      <aside className="index-rail">
        <a className="text-link index-rail__back" href="/"><ArrowIcon direction="left" /> ALL WORK</a>
        <div className="index-rail__record">
          <span className="eyebrow">INDEX</span>
          <strong>001</strong>
          <dl>
            <div><dt>CATEGORY</dt><dd>DEMONSTRATION</dd></div>
            <div><dt>DATE</dt><dd>{numericDate(video.created_at)}</dd></div>
          </dl>
        </div>
        <SiteMark />
      </aside>

      <main className="watch-main">
        <div className="player-frame">
          <video
            ref={playerRef}
            controls
            playsInline
            preload="metadata"
            poster={video.poster_key ? `/poster/${video.id}` : undefined}
            onLoadedMetadata={onLoadedMetadata}
            onPlay={() => {
              if (!playedRef.current) {
                playedRef.current = true;
                sendAnalytics(video.id, "play");
              }
              lastTimeRef.current = playerRef.current?.currentTime ?? 0;
            }}
            onTimeUpdate={() => {
              const player = playerRef.current;
              if (!player) return;
              const previous = lastTimeRef.current;
              const current = player.currentTime;
              const delta = current - previous;
              if (delta > 0 && delta < 3) watchedRef.current += delta;
              lastTimeRef.current = current;
              if (watchedRef.current >= 15) flushWatch();
              const now = Date.now();
              if (now - lastSaveRef.current > 5000 && current > 5) {
                lastSaveRef.current = now;
                saveResumePoint(video.id, current);
              }
              recordCompletion(previous, false);
            }}
            onPause={() => {
              flushWatch();
              const player = playerRef.current;
              if (player && player.currentTime > 5) saveResumePoint(video.id, player.currentTime);
            }}
            onEnded={() => {
              flushWatch();
              recordCompletion(lastTimeRef.current, true);
              clearResumePoint(video.id);
            }}
          >
            <source src={`/media/${video.id}`} type={video.mime_type} />
            {video.captions_key ? <track src={`/captions/${video.id}`} kind="captions" srcLang="en" label="English" default /> : null}
          </video>
          {resumeOffer !== null ? (
            <div className="resume-bar" data-testid="resume-bar">
              <span>PICK UP AT {formatShareTime(resumeOffer)}?</span>
              <div className="resume-bar__actions">
                <button type="button" onClick={acceptResume}>RESUME</button>
                <button type="button" onClick={dismissResume}>START OVER</button>
              </div>
            </div>
          ) : null}
        </div>
        <div className="work-ruler" aria-hidden="true">
          <span>001</span><i /><b />
        </div>
        <section className="watch-copy">
          <div>
            <p className="eyebrow">WORK RECORD · {video.visibility.toUpperCase()}</p>
            <h1>{video.title}</h1>
          </div>
          <div className="watch-copy__detail">
            <p>{video.description}</p>
            <div className="share-actions">
              <button className="outline-button" type="button" onClick={() => void copyLink()}>
                {copied ? "LINK COPIED" : "COPY VIEWING LINK"} <ArrowIcon />
              </button>
              <button
                className="outline-button outline-button--secondary"
                type="button"
                onClick={() => void copyMomentLink()}
                aria-label="Copy a link that starts at the current moment"
              >
                {momentCopied ? `LINK AT ${momentCopied} COPIED` : "COPY LINK AT CURRENT MOMENT"} <ArrowIcon />
              </button>
            </div>
          </div>
        </section>
      </main>

      {video.notes.length ? (
        <aside className={`notes-panel ${notesOpen ? "" : "notes-panel--closed"}`}>
          {notesOpen ? (
            <>
              <header>
                <span id="notes-heading">NOTES</span>
                <button type="button" aria-expanded="true" aria-controls="notes-list" onClick={() => setNotesOpen(false)}>HIDE <span aria-hidden="true">›</span></button>
              </header>
              <div className="notes-list" id="notes-list" aria-labelledby="notes-heading">
                {video.notes.map((note, position) => (
                  <button
                    className="note"
                    type="button"
                    key={note.id ?? position}
                    onClick={() => {
                      seekTo(note.seconds);
                      announce(`Jumped to ${formatShareTime(note.seconds)} — ${note.title}`);
                    }}
                  >
                    <span className="note__time">{formatNoteTime(note.seconds)}</span>
                    <span className="note__copy"><strong>{note.title}</strong><small>{note.body}</small></span>
                    <span className="note__tick" aria-hidden="true">+</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <button className="notes-open" type="button" aria-expanded="false" aria-controls="notes-list" onClick={() => setNotesOpen(true)}>NOTES <span aria-hidden="true">‹</span></button>
          )}
        </aside>
      ) : null}

      <footer className="watch-footer">
        <span>{formatDuration(video.duration_seconds)}</span>
        <span>{video.captions_key ? "CAPTIONS" : "NO CAPTIONS"}</span>
        <span>SHARED {formatDate(video.created_at)}</span>
      </footer>
    </div>
  );
}

function LoginPanel({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const [issue, setIssue] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <main className="login-page">
      <SiteMark />
      <form onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        setIssue("");
        try {
          await login(password);
          onSuccess();
        } catch (error) {
          setIssue(error instanceof Error ? error.message : "Could not sign in.");
        } finally {
          setBusy(false);
        }
      }}>
        <p className="eyebrow">OWNER ACCESS</p>
        <h1>Open the cabinet.</h1>
        <label><span>ADMIN PASSWORD</span><input autoFocus type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        {issue ? <p className="form-error">{issue}</p> : null}
        <button className="primary-button" disabled={busy} type="submit">{busy ? "CHECKING…" : "CONTINUE"}</button>
      </form>
    </main>
  );
}

function NotesEditor({ notes, onChange }: { notes: VideoNote[]; onChange: (notes: VideoNote[]) => void }) {
  const update = (index: number, patch: Partial<VideoNote>) => onChange(notes.map((note, position) => position === index ? { ...note, ...patch } : note));
  return (
    <div className="notes-editor">
      <div className="form-section-heading"><span>TIMESTAMPED NOTES</span><button type="button" onClick={() => onChange([...notes, { seconds: 0, title: "", body: "" }])}>+ ADD NOTE</button></div>
      {notes.length === 0 ? <p className="form-hint">Optional. Notes become clickable moments beside the player.</p> : null}
      {notes.map((note, index) => (
        <div className="note-editor-row" key={index}>
          <label><span>TIME (SECONDS)</span><input type="number" min="0" value={note.seconds} onChange={(event) => update(index, { seconds: Number(event.target.value) })} /></label>
          <label><span>TITLE</span><input value={note.title} onChange={(event) => update(index, { title: event.target.value })} /></label>
          <label className="note-editor-row__body"><span>NOTE</span><input value={note.body} onChange={(event) => update(index, { body: event.target.value })} /></label>
          <button className="remove-button" type="button" aria-label={`Remove note ${index + 1}`} onClick={() => onChange(notes.filter((_, position) => position !== index))}>×</button>
        </div>
      ))}
    </div>
  );
}

function UploadPanel({ onComplete }: { onComplete: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [captions, setCaptions] = useState<File | null>(null);
  const [draft, setDraft] = useState<UploadDraft>({ title: "", description: "", visibility: "unlisted", notes: [] });
  const [progress, setProgress] = useState<{ percent: number; message: string } | null>(null);
  const [issue, setIssue] = useState("");
  const cancelRef = useRef(false);
  const startRef = useRef<{ id: string; uploadId: string } | null>(null);
  const busy = Boolean(progress && progress.percent < 100);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file || !draft.title.trim()) return;
    cancelRef.current = false;
    startRef.current = null;
    setIssue("");
    setProgress({ percent: 0, message: "Preparing upload" });
    try {
      await uploadVideo(file, captions, draft, {
        onProgress: (percent, message) => setProgress({ percent, message }),
        shouldCancel: () => cancelRef.current,
        onStart: (start) => { startRef.current = { id: start.id, uploadId: start.uploadId }; },
      });
      setFile(null);
      setCaptions(null);
      setDraft({ title: "", description: "", visibility: "unlisted", notes: [] });
      onComplete();
    } catch (error) {
      let cleanupNote = "";
      const started = startRef.current as { id: string; uploadId: string } | null;
      if (started) {
        setProgress((current) => current ? { ...current, message: "Cleaning up" } : current);
        const cleanup = await abortUpload(started.id, started.uploadId);
        if (!cleanup.record || !cleanup.multipart) {
          cleanupNote = " Cleanup could not be fully confirmed — check ALL RECORDS for a leftover partial entry.";
        }
      }
      setProgress(null);
      if (error instanceof Error && error.message === "Upload cancelled.") setIssue(`Upload cancelled. The partial record was removed.${cleanupNote}`);
      else setIssue(`${error instanceof Error ? error.message : "Upload failed."}${cleanupNote}`);
    }
  };

  const cancel = () => {
    cancelRef.current = true;
    setProgress((current) => current ? { ...current, message: "Cancelling…" } : current);
  };

  return (
    <form className="upload-panel" onSubmit={submit}>
      <div className="admin-section-heading"><span>NEW RECORD</span><strong>01</strong></div>
      <label className="drop-zone">
        <input type="file" accept="video/*,.mov,.mp4,.m4v,.webm" disabled={busy} onChange={(event) => {
          const next = event.target.files?.[0] ?? null;
          setFile(next);
          if (next && !draft.title) setDraft((current) => ({ ...current, title: next.name.replace(/\.[^.]+$/, "").replaceAll(/[-_]+/g, " ") }));
        }} />
        <span className="drop-zone__plus">+</span>
        <strong>{file ? file.name : "CHOOSE A VIDEO"}</strong>
        <small>{file ? `${(file.size / 1024 / 1024).toFixed(1)} MB · ready to upload` : "MP4, MOV, M4V, or WebM · large files upload in resumable parts"}</small>
      </label>
      <div className="form-grid">
        <label className="form-grid__wide"><span>TITLE</span><input required value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /></label>
        <label className="form-grid__wide"><span>DESCRIPTION</span><textarea rows={3} value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
        <label><span>VISIBILITY</span><select value={draft.visibility} onChange={(event) => setDraft({ ...draft, visibility: event.target.value as Visibility })}><option value="unlisted">Unlisted</option><option value="public">Public</option></select></label>
        <label><span>CAPTIONS (OPTIONAL)</span><input type="file" accept=".vtt,text/vtt" onChange={(event) => setCaptions(event.target.files?.[0] ?? null)} /></label>
      </div>
      <NotesEditor notes={draft.notes} onChange={(notes) => setDraft({ ...draft, notes })} />
      {progress ? <div className="upload-progress" role="status"><div><span>{progress.message}</span><strong>{progress.percent}%</strong></div><progress aria-label="Upload progress" value={progress.percent} max="100" /></div> : null}
      {issue ? <p className="form-error">{issue}</p> : null}
      <div className="upload-panel__actions">
        <button className="primary-button" type="submit" disabled={!file || !draft.title.trim() || busy}>{busy ? "UPLOADING…" : "CREATE RECORD"}</button>
        {busy ? <button className="text-button upload-panel__cancel" type="button" onClick={cancel}>CANCEL UPLOAD</button> : null}
      </div>
    </form>
  );
}

function RecordEditor({ video, onClose, onSaved }: { video: VideoRecord; onClose: () => void; onSaved: () => void }) {
  const [draft, setDraft] = useState<UploadDraft>({
    title: video.title,
    description: video.description,
    visibility: video.visibility,
    notes: video.notes.map(({ seconds, title, body }) => ({ seconds, title, body })),
  });
  const [poster, setPoster] = useState<File | null>(null);
  const [captions, setCaptions] = useState<File | null>(null);
  const [removeCaptions, setRemoveCaptions] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const shareUrl = `${window.location.origin}/v/${video.slug}`;
  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.title.trim()) return;
    setBusy(true);
    setIssue("");
    try {
      await updateVideo(video.id, draft);
      if (poster) await replaceVideoPoster(video.id, poster);
      if (captions) await replaceVideoCaptions(video.id, captions);
      else if (removeCaptions && video.captions_key) await removeVideoCaptions(video.id);
      onSaved();
      onClose();
    } catch (error) {
      setIssue(error instanceof Error ? error.message : "Could not save this record.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setIssue("");
    try {
      await deleteVideo(video.id);
      onSaved();
      onClose();
    } catch (error) {
      setIssue(error instanceof Error ? error.message : "Could not delete this record.");
      setBusy(false);
    }
  };

  return (
    <form className="record-editor" onSubmit={save}>
      <div className="record-editor__heading">
        <div><p className="eyebrow">EDIT RECORD</p><strong>{video.title}</strong></div>
        <button className="text-button" type="button" onClick={onClose}>CLOSE ×</button>
      </div>
      <div className="form-grid">
        <label className="form-grid__wide"><span>TITLE</span><input required value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /></label>
        <label className="form-grid__wide"><span>DESCRIPTION</span><textarea rows={3} value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
        <label><span>VISIBILITY</span><select value={draft.visibility} onChange={(event) => setDraft({ ...draft, visibility: event.target.value as Visibility })}><option value="unlisted">Unlisted</option><option value="public">Public</option></select></label>
        <div className="record-editor__link"><span>STABLE SHARE LINK</span><button className="outline-button" type="button" onClick={copyLink}>{copied ? "COPIED" : "COPY LINK"}</button></div>
        <label><span>REPLACE POSTER (JPEG)</span><input type="file" accept="image/jpeg,.jpg,.jpeg" onChange={(event) => setPoster(event.target.files?.[0] ?? null)} /></label>
        <label><span>{video.captions_key ? "REPLACE CAPTIONS (WEBVTT)" : "ADD CAPTIONS (WEBVTT)"}</span><input type="file" accept=".vtt,text/vtt" onChange={(event) => { setCaptions(event.target.files?.[0] ?? null); setRemoveCaptions(false); }} /></label>
      </div>
      {video.captions_key ? <label className="caption-removal"><input type="checkbox" checked={removeCaptions} disabled={Boolean(captions)} onChange={(event) => setRemoveCaptions(event.target.checked)} /> REMOVE CURRENT CAPTIONS</label> : null}
      <NotesEditor notes={draft.notes} onChange={(notes) => setDraft({ ...draft, notes })} />
      <p className="form-hint">Editing the title does not change the existing share link.</p>
      {issue ? <p className="form-error">{issue}</p> : null}
      <div className="record-editor__footer">
        <button className="primary-button" type="submit" disabled={busy || !draft.title.trim()}>{busy ? "SAVING…" : "SAVE CHANGES"}</button>
        <div className="danger-zone">
          {confirmDelete ? <><button className="text-button" type="button" disabled={busy} onClick={() => setConfirmDelete(false)}>KEEP RECORD</button><button className="danger-button" type="button" disabled={busy} onClick={() => void remove()}>DELETE FOREVER</button></> : <button className="danger-button danger-button--quiet" type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>DELETE RECORD</button>}
        </div>
      </div>
    </form>
  );
}

function RecordList({ videos, onRefresh }: { videos: VideoRecord[]; onRefresh: () => void }) {
  const [saving, setSaving] = useState("");
  const [editing, setEditing] = useState("");
  const [issue, setIssue] = useState("");
  return (
    <section className="record-list">
      <div className="admin-section-heading"><span>ALL RECORDS</span><strong>{String(videos.length).padStart(2, "0")}</strong></div>
      <p className="form-hint">Plays and completions count anonymous viewing sessions — not unique viewers.</p>
      {issue ? <p className="form-error">{issue}</p> : null}
      {videos.length === 0 ? <p className="form-hint">No videos yet. The first upload will appear here.</p> : videos.map((video, position) => (
        <div className="record-item" key={video.id}>
          <article className="record-row">
            <span className="record-row__number">{indexNumber(position)}</span>
            <div className="record-row__poster">{video.poster_key ? <img src={`/poster/${video.id}`} alt="" /> : <div className="poster-placeholder" />}</div>
            <div className="record-row__title"><strong>{video.title}</strong><small>{video.status.toUpperCase()} · {formatDuration(video.duration_seconds)}</small></div>
            <label><span className="sr-only">Visibility for {video.title}</span><select value={video.visibility} disabled={saving === video.id} onChange={async (event) => {
              setSaving(video.id);
              setIssue("");
              try {
                await updateVideo(video.id, { visibility: event.target.value as Visibility });
                onRefresh();
              } catch (error) {
                setIssue(error instanceof Error ? error.message : "Could not change visibility.");
              } finally {
                setSaving("");
              }
            }}><option value="unlisted">UNLISTED</option><option value="public">PUBLIC</option></select></label>
            <div className="record-row__analytics" aria-label={`Aggregate session analytics for ${video.title}`}>
              <span>{video.plays ?? 0} PLAYS</span>
              <span>{(video.completions ?? 0) === 0 ? "NO COMPLETIONS YET" : `${video.completions ?? 0} COMPLETIONS`}</span>
              <span>{completionRate(video.completions ?? 0, video.plays ?? 0) ?? "—"} RATE</span>
              <span>{averageWatched(video.watched_seconds ?? 0, video.plays ?? 0) !== null ? `${formatDuration(averageWatched(video.watched_seconds ?? 0, video.plays ?? 0) as number)} AVG WATCH` : "— AVG WATCH"}</span>
            </div>
            <div className="record-row__actions">
              {video.status === "ready" ? <a className="outline-button" href={`/v/${video.slug}`}>VIEW <ArrowIcon /></a> : video.status === "error" ? <span className="status-label status-label--error">FAILED</span> : <span className="status-label">PROCESSING</span>}
              <button className="outline-button" type="button" onClick={() => setEditing(editing === video.id ? "" : video.id)}>{editing === video.id ? "CLOSE" : "EDIT"}</button>
            </div>
          </article>
          {editing === video.id ? <RecordEditor video={video} onClose={() => setEditing("")} onSaved={onRefresh} /> : null}
        </div>
      ))}
    </section>
  );
}

function AdminPage() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [videos, setVideos] = useState<VideoRecord[]>([]);
  const [issue, setIssue] = useState("");

  const refresh = useCallback(async () => {
    try {
      setVideos(await adminVideos());
      setAuthenticated(true);
      setIssue("");
    } catch (error) {
      const typed = error as Error & { status?: number };
      if (typed.status === 401) setAuthenticated(false);
      else setIssue(typed.message);
    }
  }, []);

  useEffect(() => {
    let active = true;
    adminVideos().then((nextVideos) => {
      if (!active) return;
      setVideos(nextVideos);
      setAuthenticated(true);
      setIssue("");
    }).catch((error: Error & { status?: number }) => {
      if (!active) return;
      if (error.status === 401) setAuthenticated(false);
      else setIssue(error.message);
    });
    return () => { active = false; };
  }, []);
  if (authenticated === null && !issue) return <Loading label="Checking owner access" />;
  if (authenticated === false) return <LoginPanel onSuccess={() => void refresh()} />;
  if (issue) return <ErrorPage message={issue} />;

  return (
    <div className="admin-page">
      <header className="admin-header"><SiteMark /><div><a className="text-link" href="/">VIEW PUBLIC INDEX <ArrowIcon /></a><button type="button" onClick={async () => { await logout(); setAuthenticated(false); }}>SIGN OUT</button></div></header>
      <section className="admin-intro"><p className="eyebrow">OWNER CABINET</p><h1>Publish the next<br />piece of work.</h1><p>Upload from ScreenKite, Capptivo, or anywhere else. Every record receives its own shareable link.</p></section>
      <UploadPanel onComplete={() => void refresh()} />
      <RecordList videos={videos} onRefresh={() => void refresh()} />
    </div>
  );
}

export default function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const match = path.match(/^\/v\/([^/]+)$/);
  const route = path === "/manage"
    ? { type: "manage" as const }
    : match
      ? { type: "watch" as const, slug: decodeURIComponent(match[1]) }
      : { type: "home" as const };
  if (route.type === "manage") return <AdminPage />;
  if (route.type === "watch") return <WatchPage slug={route.slug} />;
  return <HomePage />;
}

export { BLUE };
